import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Container from '@mui/material/Container';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  Chip,
  Typography,
  IconButton,
  TextField,
  Switch,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CustomMultiSelect from 'src/components/custom-multiselect';

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField, RHFSelect } from 'src/components/hook-form';
import { useBoolean } from 'src/hooks/use-boolean';
import { paths } from 'src/routes/paths';
import { ARABIC_REGEX, ENGLISH_REGEX } from 'src/utils/regex';
import { ConfirmDialog } from 'src/components/custom-dialog';
import PermissionBasedGuard from 'src/auth/guard/permission-based-guard';
import Iconify from 'src/components/iconify';
import {
  createNewRestaurantPlanGroup,
  deleteRestaurantPlanGroup,
  updateRestaurantPlanGroup,
  useGetRestaurantPlanGroup,
  useGetMealTags,
  useGetPlanTags,
} from 'src/api/restaurant';
import type { CommonMealTag, RestaurantPlanGroup } from 'src/api/restaurant';

// ----------------------------------------------------------------------

interface PlanFormProps {
  onClose: () => void;
  onSave: (data: {
    meals: CommonMealTag[];
    status: 'active' | 'inactive';
    packages: any[];
    calories?: {
      calories?: number | null;
      calories_from?: number | null;
      calories_to?: number | null;
    } | null;
  }) => void;
  initialPlan?: RestaurantPlanGroup['plans'][number] | undefined;
  onHasChanges?: (hasChanges: boolean) => void;
  onDraftChange?: (key: number | string, draft: any) => void;
  draftKey?: number | string;
}

interface PackageFormProps {
  onClose: () => void;
  onSave: (data: {
    period: number;
    price: string;
    discounted_price?: string;
    off_days?: { id: number }[];
  }) => void;
  initialPackage?: RestaurantPlanGroup['plans'][number]['packages'][number] | undefined;
  onHasChanges?: (hasChanges: boolean) => void;
}

interface Props {
  restaurantId: string;
  id: string | undefined;
}

const PackageForm = ({
  onClose: handleClose,
  onSave: handleSave,
  initialPackage,
  onHasChanges,
}: PackageFormProps) => {
  const [period, setPeriod] = useState(initialPackage?.period);
  const [price, setPrice] = useState(initialPackage?.price || '');
  const [discountedPrice, setDiscountedPrice] = useState(initialPackage?.discounted_price || '');
  const [offDays, setOffDays] = useState<number[]>(
    initialPackage?.off_days?.map((day) => day.id) || []
  );
  const [errors, setErrors] = useState<{
    period?: string;
    price?: string;
    discounted_price?: string;
  }>({});
  // removed local submit loading; auto-save is handled by parent

  // Track if form has changes (local)
  const [, setPackageHasChanges] = useState(false);

  // Validator is declared before effects to avoid use-before-define and stale closures
  const validateForm = useCallback(() => {
    const newErrors: {
      period?: string;
      price?: string;
      discounted_price?: string;
    } = {};

    if (!period) {
      newErrors.period = 'Period is required';
    } else if (period > 60) {
      newErrors.period = 'Period cannot exceed 60 days';
    } else if (period < 1) {
      newErrors.period = 'Period must be at least 1 day';
    }

    if (!price) {
      newErrors.price = 'Price is required';
    } else if (Number(price) > 300) {
      newErrors.price = 'Price cannot exceed 300 KD';
    } else if (Number(price) < 0) {
      newErrors.price = 'Price cannot be negative';
    }

    if (discountedPrice && Number(discountedPrice) >= Number(price)) {
      newErrors.discounted_price = 'Discounted price must be lower than regular price';
    } else if (discountedPrice && Number(discountedPrice) < 0) {
      newErrors.discounted_price = 'Discounted price cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [period, price, discountedPrice]);

  // Check for changes whenever form values change
  useEffect(() => {
    const currentData = {
      period,
      price,
      discounted_price: discountedPrice,
      off_days: offDays.sort(),
    };

    const initialData = {
      period: initialPackage?.period,
      price: initialPackage?.price || '',
      discounted_price: initialPackage?.discounted_price || '',
      off_days: (initialPackage?.off_days?.map((day) => day.id) || []).sort(),
    };

    const changed = JSON.stringify(currentData) !== JSON.stringify(initialData);
    setPackageHasChanges(changed);
    
    // Live-validate and display errors
    const isValid = validateForm();
    
    // Auto-save for both existing and new placeholders when valid and changed
    if (initialPackage && changed && isValid) {
      handleSave({
        period: period || 0,
        price,
        discounted_price: discountedPrice || undefined,
        off_days: offDays.length > 0 ? offDays.map((dayId) => ({ id: dayId })) : undefined,
      });
    }
    
    // Notify parent component about changes
    if (onHasChanges) {
      onHasChanges(changed);
    }
  }, [period, price, discountedPrice, offDays, initialPackage, onHasChanges, handleSave, validateForm]);

  // no explicit submit; auto-save occurs upon valid changes

  return (
    <Box 
      sx={{ 
        p: 0,
        bgcolor: 'transparent',
        borderRadius: 0,
        border: "none",
      }}
    >

      <Stack spacing={1.5}>
        {/* First row: Period, Price, Discounted Price */}
        <Grid container spacing={1.5}>
          <Grid xs={4}>
            <TextField
              label="Period (days)"
              value={period || ''}
              onChange={(e) => setPeriod(Number(e.target.value))}
              required
              error={Boolean(errors.period)}
              helperText={errors.period}
              inputProps={{ min: 1, max: 60 }}
              fullWidth
            />
          </Grid>
          <Grid xs={4}>
            <TextField
              label="Price"
              value={price || ''}
              onChange={(e) => setPrice(e.target.value)}
              required
              error={Boolean(errors.price)}
              helperText={errors.price}
              inputProps={{ min: 0, max: 300 }}
              fullWidth
            />
          </Grid>
          <Grid xs={4}>
            <TextField
              label="Discounted Price"
              value={discountedPrice || ''}
              onChange={(e) => setDiscountedPrice(e.target.value)}
              error={Boolean(errors.discounted_price)}
              helperText={errors.discounted_price}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Second row: Off Days */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="caption" sx={{ mr: 1, minWidth: 'fit-content', fontWeight: 600, color: 'text.secondary' }}>Off Days:</Typography>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
            <FormControlLabel
              key={index + 1}
              control={
                <Checkbox
                  size="small"
                  checked={offDays.includes(index + 1)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setOffDays([...offDays, index + 1]);
                    } else {
                      setOffDays(offDays.filter((d) => d !== index + 1));
                    }
                  }}
                />
              }
              label={<Typography variant="caption">{day}</Typography>}
              sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
            />
          ))}
        </Box>
      </Stack>

      {/* No explicit Save; edits are auto-applied. Close via parent UI controls. */}
    </Box>
  );
};

const PlanForm = ({ onClose, onSave, initialPlan, onHasChanges, onDraftChange, draftKey }: PlanFormProps) => {
  const { mealTags: availableMealTags } = useGetMealTags();
  const [selectedMeals, setSelectedMeals] = useState<CommonMealTag[]>(initialPlan?.meals || []);
  const [status, setStatus] = useState<'active' | 'inactive'>(initialPlan?.status || 'active');
  const [packages, setPackages] = useState<any[]>(initialPlan?.packages || []);
  const [openPackageEditors, setOpenPackageEditors] = useState<number[]>([]);
  const [packageChanges, setPackageChanges] = useState<Record<number, boolean>>({});

  // Calories state
  const [useCalorieRange, setUseCalorieRange] = useState<boolean>(
    Boolean(initialPlan?.calories?.calories_from || initialPlan?.calories?.calories_to)
  );
  const [calories, setCalories] = useState<number | null>(initialPlan?.calories?.calories || null);
  const [caloriesFrom, setCaloriesFrom] = useState<number | null>(
    initialPlan?.calories?.calories_from || null
  );
  const [caloriesTo, setCaloriesTo] = useState<number | null>(
    initialPlan?.calories?.calories_to || null
  );

  // Check for changes whenever form values change
  useEffect(() => {
    const currentData = {
      meals: selectedMeals,
      status,
      packages,
      calories: {
        calories,
        calories_from: caloriesFrom,
        calories_to: caloriesTo,
      },
    };

    const initialData = {
      meals: initialPlan?.meals || [],
      status: initialPlan?.status || 'active',
      packages: initialPlan?.packages || [],
      calories: {
        calories: initialPlan?.calories?.calories || null,
        calories_from: initialPlan?.calories?.calories_from || null,
        calories_to: initialPlan?.calories?.calories_to || null,
      },
    };

    const changed = JSON.stringify(currentData) !== JSON.stringify(initialData);

    // Notify parent component about changes (including child changes)
    const anyChildChanged = Object.values(packageChanges).some(Boolean);
    const totalHasChanges = changed || anyChildChanged;
    if (onHasChanges) {
      onHasChanges(totalHasChanges);
    }

    // Emit draft up to parent so Save Plan Group can persist unsaved edits
    if (onDraftChange && (typeof draftKey !== 'undefined' || typeof initialPlan?.id !== 'undefined')) {
      const key = draftKey ?? (initialPlan?.id as number);
      if (totalHasChanges) {
        const draftCalories = (() => {
          if (useCalorieRange) {
            if (caloriesFrom && caloriesTo) {
              return {
                calories: undefined,
                calories_from: caloriesFrom,
                calories_to: caloriesTo,
              };
            }
            return undefined;
          }
          if (calories) {
            return {
              calories,
              calories_from: undefined,
              calories_to: undefined,
            };
          }
          return undefined;
        })();
        onDraftChange(key, {
          meals: selectedMeals,
          status,
          packages,
          calories: draftCalories,
        });
      } else {
        // Clear draft when reverted to initial values
        onDraftChange(key, undefined as any);
      }
    }
  }, [selectedMeals, status, packages, calories, caloriesFrom, caloriesTo, initialPlan, packageChanges, onHasChanges, onDraftChange, draftKey, useCalorieRange]);

  const handlePackageSubmitAtIndex = useCallback((index: number, data: {
    period: number;
    price: string;
    discounted_price?: string;
    off_days?: { id: number }[];
  }) => {
    setPackages((prev) =>
      prev.map((pkg, i) =>
        i === index
          ? {
              ...pkg,
              ...data,
              status: pkg.status || 'active',
              is_deleted: pkg.is_deleted ?? false,
            }
          : pkg
      )
    );
  }, []);

  const handleDeletePackage = useCallback((index: number) => {
    setPackages((prev) => {
      const target = prev[index];
      if (!target) return prev;
      // If package has id => saved in backend: soft-delete and keep for API
      if (target.id) {
        const updated = prev.map((pkg, i) => (i === index ? { ...pkg, is_deleted: true } : pkg));
        return updated;
      }
      // If package has no id => new/unsaved: remove from local state entirely
      const next = prev.filter((_, i) => i !== index);
      // Adjust open editors indices
      setOpenPackageEditors((open) =>
        open
          .filter((i) => i !== index)
          .map((i) => (i > index ? i - 1 : i))
      );
      // Adjust package changes map
      setPackageChanges((map) => {
        const newMap: Record<number, boolean> = {};
        Object.entries(map).forEach(([k, v]) => {
          const iNum = Number(k);
          if (iNum === index) return;
          if (iNum > index) newMap[iNum - 1] = v as boolean;
          else newMap[iNum] = v as boolean;
        });
        return newMap;
      });
      return next;
    });
  }, []);


  const handlePackageChanges = useCallback((index: number, pkgHasChanges: boolean) => {
    setPackageChanges((prev) => ({ ...prev, [index]: pkgHasChanges }));
  }, []);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {initialPlan ? 'Edit Plan' : 'Add New Plan'}
      </Typography>

      <Stack spacing={2}>
        <CustomMultiSelect
          options={availableMealTags || []}
          value={selectedMeals}
          onChange={(_, newValue) => setSelectedMeals(newValue)}
          getOptionLabel={(option) => `${option.name_en} (${option.name_ar})`}
          getTagLabel={(option) => `${option.name_en} (${option.name_ar})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          label="Select Meals"
          placeholder="Search and select meals"
        />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={status === 'active'}
            onChange={(e) => setStatus(e.target.checked ? 'active' : 'inactive')}
          />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Active
          </Typography>
        </Box>

        <Box>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Calories</Typography>
            <Switch
              size="small"
              checked={useCalorieRange}
              onChange={(e) => {
                setUseCalorieRange(e.target.checked);
                // Reset values when switching
                if (e.target.checked) {
                  setCalories(null);
                } else {
                  setCaloriesFrom(null);
                  setCaloriesTo(null);
                }
              }}
            />
            <Typography variant="body2">Use Range</Typography>
          </Stack>

          {useCalorieRange ? (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Calories From"
                value={caloriesFrom ?? ''}
                onChange={(e) => setCaloriesFrom(e.target.value ? Number(e.target.value) : null)}
                fullWidth
              />
              <TextField
                label="Calories To"
                value={caloriesTo ?? ''}
                onChange={(e) => setCaloriesTo(e.target.value ? Number(e.target.value) : null)}
                fullWidth
                error={Boolean(caloriesFrom && caloriesTo && caloriesFrom >= caloriesTo)}
                helperText={
                  caloriesFrom && caloriesTo && caloriesFrom >= caloriesTo
                    ? 'Calories From must be less than Calories To'
                    : ''
                }
              />
            </Stack>
          ) : (
            <TextField
              label="Calories"
              value={calories ?? ''}
              onChange={(e) => setCalories(e.target.value ? Number(e.target.value) : null)}
              fullWidth
            />
          )}
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Packages</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={() => {
                setPackages((prev) => {
                  const nextIndex = prev.length;
                  const next = [
                    ...prev,
                    { status: 'active', is_deleted: false, price: '', discounted_price: '', off_days: [] },
                  ];
                  setOpenPackageEditors((open) => [...open, nextIndex]);
                  return next;
                });
              }}
            >
              Add Package
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            {packages.map((pkg, index) => {
              const isPackageOpen = openPackageEditors.includes(index);
              return (
              pkg?.is_deleted ? null : (
              <Accordion 
                key={index} 
                expanded={isPackageOpen}
                onChange={(_, isExpanded) => {
                  if (isExpanded) {
                    setOpenPackageEditors((prev) => [...prev, index]);
                  } else {
                    setOpenPackageEditors((prev) => prev.filter((i) => i !== index));
                  }
                }}
                elevation={isPackageOpen ? 4 : 1}
                sx={{
                  ml: 2,
                  border: '1px solid',
                  borderColor: isPackageOpen ? 'primary.dark' : 'divider',
                  bgcolor: isPackageOpen ? 'action.selected' : 'background.paper',
                  '&.Mui-expanded': {
                    boxShadow: (theme) => theme.customShadows?.z8 || theme.shadows[4],
                  },
                  '&:before': {
                    display: 'none',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: isPackageOpen ? 56 : 64,
                    bgcolor: isPackageOpen ? 'action.selected' : 'transparent',
                    color: 'inherit',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      margin: '8px 0',
                      '&.Mui-expanded': {
                        margin: '8px 0',
                      },
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'inherit',
                    },
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {!isPackageOpen && (
                    <Box sx={{ flex: 1, mr: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {pkg.period} days
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            textDecoration: pkg.discounted_price ? 'line-through' : 'none',
                            color: pkg.discounted_price ? 'text.disabled' : 'text.primary'
                          }}
                        >
                          {pkg.price} KD
                        </Typography>
                        {pkg.discounted_price && (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {pkg.discounted_price} KD
                          </Typography>
                        )}
                        {pkg.id ? (
                          packageChanges[index] && (
                            <Chip 
                              label="Modified" 
                              size="small" 
                              color="warning" 
                              sx={{ height: 20 }} 
                            />
                          )
                        ) : (
                          <Chip 
                            label="New" 
                            size="small" 
                            color="success" 
                            sx={{ height: 20 }} 
                          />
                        )}
                      </Stack>
                      {pkg.off_days && pkg.off_days.length > 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          Off: {pkg.off_days
                            .map(
                              (day: { id: number }) =>
                                [
                                  'Sun',
                                  'Mon',
                                  'Tue',
                                  'Wed',
                                  'Thu',
                                  'Fri',
                                  'Sat',
                                ][day.id - 1]
                            )
                            .join(', ')}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {isPackageOpen && (
                    <Box sx={{ flex: 1, mr: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'inherit' }}>
                        Editing Package
                      </Typography>
                    </Box>
                  )}
                  
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDeletePackage(index);
                    }}
                    sx={{ 
                      pointerEvents: 'auto',
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.lighter',
                      },
                      '& .component-iconify': {
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </AccordionSummary>
                
                <AccordionDetails
                  sx={{
                    bgcolor: 'background.neutral',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    p: 2,
                  }}
                >
                  <PackageForm
                    onClose={() => {}}
                    onSave={(data) => handlePackageSubmitAtIndex(index, data)}
                    initialPackage={pkg}
                    onHasChanges={(changed) => handlePackageChanges(index, changed)}
                  />
                </AccordionDetails>
              </Accordion>
              )
            )})}
          </Stack>
        </Box>
      </Stack>
      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="outlined" color="inherit" size="small" onClick={onClose}>
          Close
        </Button>
      </Stack>
    </Box>
  );
};

export default function RestaurantEditPlanGroup({ restaurantId, id }: Props) {
  const navigate = useNavigate();
  const confirm = useBoolean();
  const { enqueueSnackbar } = useSnackbar();
  const isEditPlanGroup = Boolean(id);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [plans, setPlans] = useState<any[]>([]);
  const [showCustomization, setShowCustomization] = useState(Boolean(id));
  // Removed unused hasUnsavedChanges state

  // Track form changes
  const [hasFormChanges, setHasFormChanges] = useState(false);
  // Track which plan editors are open (keys: existing plan id or `local-<index>` or `new-<n>`)
  const [openPlanEditors, setOpenPlanEditors] = useState<Array<string | number>>([]);
  // Track ephemeral new plan editors (edit mode only)
  const [newPlanEditors, setNewPlanEditors] = useState<string[]>([]);
  // Track per-plan change flags to know if any plan has unsaved changes
  const [planChanges, setPlanChanges] = useState<Record<string | number, boolean>>({});
  // Track which plan (id or ephemeral/local key) is pending deletion confirmation
  const [planToDelete, setPlanToDelete] = useState<number | string | undefined>(undefined);

  // Track unsaved plan edits as drafts keyed by plan id or temporary keys
  const [planDrafts, setPlanDrafts] = useState<Map<number | string, any>>(new Map());
  const upsertPlanDraft = useCallback((key: number | string, draft: any) => {
    setPlanDrafts((prev) => {
      const next = new Map(prev);
      next.set(key, draft);
      return next;
    });
  }, []);
  const clearPlanDraft = useCallback((key: number | string) => {
    setPlanDrafts((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const { restaurantPlanGroup, mutate } = useGetRestaurantPlanGroup(id ? Number(id) : 0);
  const { planTags } = useGetPlanTags();

  // Scroll management for newly added plan editors
  const [lastAddedPlanKey, setLastAddedPlanKey] = useState<string | number | null>(null);
  const planItemRefs = useRef<Map<string | number, HTMLDivElement | null>>(new Map());

  const defaultValues = useMemo(
    () => ({
      special_name_en: restaurantPlanGroup?.special_name_en || '',
      special_name_ar: restaurantPlanGroup?.special_name_ar || '',
      restaurant: {
        id: Number(restaurantId) || 0,
      },
      plan_tag: {
        id: restaurantPlanGroup?.plan_tag?.id || 0,
      },
      protein_size: restaurantPlanGroup?.protein_size || null,
      carb_size: restaurantPlanGroup?.carb_size || null,
      status: restaurantPlanGroup?.status || 'active',
      is_deleted: false,
    }),
    [restaurantId, restaurantPlanGroup]
  );

  const UpdatePlanGroupSchema = Yup.object().shape({
    special_name_en: Yup.string()
      .max(50, 'English Name should be less than 50 characters')
      .matches(ENGLISH_REGEX.pattern, ENGLISH_REGEX.message)
      .nullable(),
    special_name_ar: Yup.string()
      .max(50, 'Arabic Name should be less than 50 characters')
      .matches(ARABIC_REGEX.pattern, ARABIC_REGEX.message)
      .nullable(),
    restaurant: Yup.object().shape({
      id: Yup.number().required('Restaurant is required'),
    }),
    plan_tag: Yup.object().shape({
      id: Yup.number().min(1, 'Plan Tag is required').required('Plan Tag is required'),
    }),
    protein_size: Yup.number().nullable(),
    carb_size: Yup.number().nullable(),
    status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
    is_deleted: Yup.boolean(),
  });

  const methods = useForm({
    resolver: yupResolver(UpdatePlanGroupSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = methods;

  const watchedValues = watch();

  // Track form changes
  useEffect(() => {
    if (isEditPlanGroup && restaurantPlanGroup) {
      const currentValues = JSON.stringify(watchedValues);
      const initialValues = JSON.stringify(defaultValues);
      const formChanged = currentValues !== initialValues;
      const anyPlanChanged = Object.values(planChanges).some(Boolean);
      setHasFormChanges(formChanged || anyPlanChanged);
    } else {
      setHasFormChanges(true); // New form always has changes
    }
  }, [watchedValues, defaultValues, isEditPlanGroup, restaurantPlanGroup, planChanges]);

  useEffect(() => {
    if (isEditPlanGroup && restaurantPlanGroup) {
      reset(defaultValues);
    }
  }, [isEditPlanGroup, restaurantPlanGroup, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isEditPlanGroup) {
        // Merge form values and any unsaved drafts into a single PATCH for the plan group
        const originalPlans = restaurantPlanGroup?.plans || [];
        const draftsObj: Record<string | number, any> = {};
        planDrafts.forEach((value, key) => {
          if (typeof value !== 'undefined') draftsObj[String(key)] = value;
        });

        const updatedPlans = originalPlans.map((plan) => {
          const draft = draftsObj[String(plan.id)];
          if (!draft) return plan;
          const merged: any = { ...plan };
          if (typeof draft.status !== 'undefined') merged.status = draft.status;
          if (typeof draft.meals !== 'undefined') merged.meals = draft.meals;
          if (typeof draft.calories !== 'undefined') merged.calories = draft.calories;
          if (typeof draft.packages !== 'undefined') merged.packages = draft.packages;
          return merged;
        });

        // include new plan drafts (new-*)
        Object.entries(draftsObj).forEach(([k, v]) => {
          if (String(k).startsWith('new-')) updatedPlans.push({ ...v });
        });

        // Minimal group diff (PATCH semantics)
        const groupDiff: any = {};
        const current = data as any;
        const initial = defaultValues as any;
        if (current.special_name_en !== initial.special_name_en) groupDiff.special_name_en = current.special_name_en;
        if (current.special_name_ar !== initial.special_name_ar) groupDiff.special_name_ar = current.special_name_ar;
        if (current.restaurant?.id !== initial.restaurant?.id) groupDiff.restaurant = current.restaurant;
        if (current.plan_tag?.id !== initial.plan_tag?.id) groupDiff.plan_tag = current.plan_tag;
        if (current.protein_size !== initial.protein_size) groupDiff.protein_size = current.protein_size;
        if (current.carb_size !== initial.carb_size) groupDiff.carb_size = current.carb_size;
        if (current.status !== initial.status) groupDiff.status = current.status;

        const updated_plan_group = await updateRestaurantPlanGroup({
          id: Number(id),
          ...(Object.keys(groupDiff).length > 0 ? groupDiff : {}),
          plans: updatedPlans,
        });
        if (updated_plan_group) {
          enqueueSnackbar('Plan Group Updated Successfully', { variant: 'success' });
          setHasFormChanges(false);
          setPlanDrafts(new Map());
          setOpenPlanEditors([]);
          setNewPlanEditors([]);
          setPlanChanges({});
          navigate(-1);
        }
      } else {
        if (plans.length === 0) {
          enqueueSnackbar('At least one plan is required', { variant: 'error' });
          return;
        }

        // Merge any drafts for existing local plans (keyed by `local-<index>`, but we used numeric indices earlier)
        const creationPlans = plans.map((plan, index) => {
          const draftKey = `local-${index}`;
          const draft = planDrafts.get(draftKey);
          if (!draft) return plan;
          const merged: any = { ...plan };
          if (typeof draft.status !== 'undefined') merged.status = draft.status;
          if (typeof draft.meals !== 'undefined') merged.meals = draft.meals;
          if (typeof draft.calories !== 'undefined') merged.calories = draft.calories;
          if (typeof draft.packages !== 'undefined') merged.packages = draft.packages;
          return merged;
        });

        const new_plan_group = await createNewRestaurantPlanGroup({
          ...data,
          plans: creationPlans,
        });
        if (new_plan_group) {
          enqueueSnackbar('Plan Group Created Successfully', { variant: 'success' });
          setHasFormChanges(false);
          setPlanDrafts(new Map());
          setOpenPlanEditors([]);
          setNewPlanEditors([]);
          setPlanChanges({});
          navigate(
            paths.dashboard.restaurant.plangroup.edit(restaurantId, new_plan_group.id.toString())
          );
        }
      }
      mutate();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' });
    }
  });

  // This handler remains to support duplicate button behavior; it will not close editors
  const handlePlanSubmit = async (_data: {
    meals: CommonMealTag[];
    status: 'active' | 'inactive';
    packages: any[];
    calories?: {
      calories?: number | null;
      calories_from?: number | null;
      calories_to?: number | null;
    } | null;
  }) => Promise.resolve();

  const handleDeletePlan = useCallback(
    async (planKey: number | string) => {
      if (isEditPlanGroup) {
        try {
          if (!restaurantPlanGroup) {
            enqueueSnackbar('Plan group not found', { variant: 'error' });
            return;
          }

          // If this is a new ephemeral editor (key starts with new-), just drop the draft/editor
          if (typeof planKey === 'string' && String(planKey).startsWith('new-')) {
            clearPlanDraft(planKey);
            setOpenPlanEditors((prev) => prev.filter((k) => k !== planKey));
            setNewPlanEditors((prev) => prev.filter((k) => k !== planKey));
            setOpenConfirm(false);
            enqueueSnackbar('New plan discarded');
            return;
          }

          const updatedPlans = restaurantPlanGroup.plans.map((plan) =>
            plan.id === planKey ? { ...plan, is_deleted: true } : plan
          );

          const updated_plan_group = await updateRestaurantPlanGroup({
            id: Number(id),
            plans: updatedPlans,
          });

          if (updated_plan_group) {
            enqueueSnackbar('Plan Deleted Successfully', { variant: 'success' });
            setOpenConfirm(false);
            mutate();
          }
        } catch (error: any) {
          enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' });
        }
      } else {
        // Remove plan from local state during creation using index extracted from key `local-<index>` or numeric index
        let indexToRemove: number | undefined;
        if (typeof planKey === 'string') {
          const match = planKey.match(/^local-(\d+)$/);
          if (match) indexToRemove = Number(match[1]);
        } else if (typeof planKey === 'number') {
          indexToRemove = planKey;
        }
        if (typeof indexToRemove === 'number') {
          setPlans((prev) => prev.filter((_, idx) => idx !== indexToRemove));
          setOpenPlanEditors((prev) => prev.filter((k) => k !== `local-${indexToRemove!}`));
        }
        setOpenConfirm(false);
        enqueueSnackbar('Plan removed successfully');
      }
    },
    [enqueueSnackbar, id, restaurantPlanGroup, mutate, isEditPlanGroup, clearPlanDraft]
  );

  // obsolete handlers removed; concurrent editors are managed via openPlanEditors/newPlanEditors

  const handleDuplicatePlan = useCallback(
    async (plan: any, index: number) => {
      try {
        const planCopy = { ...plan };

        // For creating a copy of the name to indicate it's a duplicate
        if (planCopy.name_en) {
          planCopy.name_en = `${planCopy.name_en} (Copy)`;
        }
        if (planCopy.name_ar) {
          planCopy.name_ar = `${planCopy.name_ar} (نسخة)`;
        }

        if (isEditPlanGroup && restaurantPlanGroup) {
          // In edit mode, create a new plan from the existing one
          const newPlan = { ...planCopy };

          // Remove ID to create as new plan
          delete newPlan.id;

          // Make copies of packages with new references
          newPlan.packages = newPlan.packages ?
            newPlan.packages.map((pkg: any) => {
              const newPkg = { ...pkg };
              delete newPkg.id;
              return newPkg;
            }) : [];

          const updated_plan_group = await updateRestaurantPlanGroup({
            id: Number(id),
            plans: [...(restaurantPlanGroup.plans || []), newPlan],
          });

          if (updated_plan_group) {
            enqueueSnackbar('Plan duplicated successfully', { variant: 'success' });
            mutate();
          }
        } else {
          // In add mode, simply add a new plan to the local state
          const newPlan = { ...planCopy };

          // Make copies of packages with new references
          newPlan.packages = newPlan.packages ?
            newPlan.packages.map((pkg: any) => {
              const newPkg = { ...pkg };
              delete newPkg.id;
              return newPkg;
            }) : [];

          setPlans([...plans, newPlan]);
          enqueueSnackbar('Plan duplicated successfully', { variant: 'success' });
        }
      } catch (error: any) {
        enqueueSnackbar(error.message || 'Failed to duplicate plan', { variant: 'error' });
      }
    },
    [enqueueSnackbar, id, restaurantPlanGroup, isEditPlanGroup, plans, mutate]
  );

  const handleDuplicatePlanGroup = useCallback(async () => {
    try {
      if (!restaurantPlanGroup) {
        enqueueSnackbar('Plan group not found', { variant: 'error' });
        return;
      }

      const newPlanGroup: Record<string, any> = {
        ...restaurantPlanGroup,
        special_name_en: `${restaurantPlanGroup.special_name_en} (Copy)`,
        special_name_ar: `${restaurantPlanGroup.special_name_ar} (نسخة)`,
      };

      // remove readonly id by creating a plain object copy without id
      delete (newPlanGroup as any).id;
      newPlanGroup.plans = (newPlanGroup.plans || []).map((plan: any) => {
        // omit id from plan and nested packages
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _omit, ...copiedPlan } = plan;
        copiedPlan.packages = (copiedPlan.packages || []).map((pkg: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id: _omit2, ...copiedPkg } = pkg;
          return copiedPkg;
        });
        return copiedPlan;
      });

      const new_plan_group = await createNewRestaurantPlanGroup(newPlanGroup);
      if (new_plan_group) {
        enqueueSnackbar('Plan Group Duplicated Successfully', { variant: 'success' });
        navigate(
          paths.dashboard.restaurant.plangroup.edit(restaurantId, new_plan_group.id.toString())
        );
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' });
    }
  }, [restaurantPlanGroup, enqueueSnackbar, navigate, restaurantId]);

  const handleDeletePlanGroup = useCallback(async () => {
    try {
      if (id) {
        await deleteRestaurantPlanGroup(Number(id));
        enqueueSnackbar('Plan Group Deleted Successfully', { variant: 'success' });
        navigate(-1);
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' });
    } finally {
      mutate();
      confirm.onFalse();
    }
  }, [confirm, enqueueSnackbar, id, navigate, mutate]);

  const formId = useId();

  const addPlanEditor = useCallback(() => {
    if (isEditPlanGroup) {
      const key = `new-${Date.now()}`;
      setNewPlanEditors((prev) => [...prev, key]);
      setOpenPlanEditors((prev) => [...prev, key]);
      upsertPlanDraft(key, { meals: [], status: 'active', packages: [], calories: undefined });
      setLastAddedPlanKey(key);
    } else {
      setPlans((prev) => [...prev, { meals: [], status: 'active', packages: [], calories: null }]);
      const key = `local-${plans.length}`;
      setOpenPlanEditors((prev) => [...prev, key]);
      upsertPlanDraft(key, { meals: [], status: 'active', packages: [], calories: null });
      setLastAddedPlanKey(key);
    }
  }, [isEditPlanGroup, plans.length, upsertPlanDraft]);


  const markPlanChanged = useCallback((key: string | number, changed: boolean) => {
    setPlanChanges((prev) => ({ ...prev, [key]: changed }));
  }, []);

  // When a new plan editor is added, scroll it into view smoothly
  useEffect(() => {
    let rafId: number | undefined;
    const el = lastAddedPlanKey != null ? planItemRefs.current.get(lastAddedPlanKey) : null;
    if (el) {
      // Use rAF to ensure element has laid out before scrolling
      rafId = requestAnimationFrame(() => {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        } catch (_) {
          // no-op
        }
      });
    }
    return () => {
      if (typeof rafId === 'number') cancelAnimationFrame(rafId);
    };
  }, [lastAddedPlanKey]);

  return (
    <PermissionBasedGuard
      permissions={
        isEditPlanGroup ? ['partners:update:plangroups'] : ['partners:create:plangroups']
      }
    >
      <FormProvider methods={methods} onSubmit={onSubmit} id={formId}>
        <Container maxWidth={false}>
          <Grid container spacing={3}>
            <Grid xs={12} md={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3 }}>
                Plan Group Details
              </Typography>

              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                }}
              >
                <RHFSelect name="plan_tag.id" label="Plan Tag">
                  <MenuItem value={0}>
                    <em>Select a plan tag</em>
                  </MenuItem>
                  {planTags?.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      {tag.name_en} ({tag.name_ar})
                    </MenuItem>
                  ))}
                </RHFSelect>

                {!isEditPlanGroup && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Switch
                      checked={showCustomization}
                      onChange={(e) => setShowCustomization(e.target.checked)}
                    />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      Customize The Naming
                    </Typography>
                  </Box>
                )}

                <RHFTextField
                  name="protein_size"
                  label="Protein Size"
                  InputLabelProps={{ shrink: true }}
                />
                <RHFTextField
                  name="carb_size"
                  label="Carb Size"
                  InputLabelProps={{ shrink: true }}
                />
                {(isEditPlanGroup || showCustomization) && (
                  <>
                    <RHFTextField name="special_name_en" label="Special English Name" />
                    <RHFTextField name="special_name_ar" label="Special Arabic Name" />
                  </>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Switch
                  checked={methods.watch('status') === 'active'}
                  onChange={(e) =>
                    methods.setValue('status', e.target.checked ? 'active' : 'inactive')
                  }
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Active
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid xs={12} md={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3 }}>
                Plans
              </Typography>

              <Button
                sx={{ mb: 3 }}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
                onClick={addPlanEditor}
              >
                Add New Plan
              </Button>

              <Stack spacing={3}>
                {(isEditPlanGroup ? restaurantPlanGroup?.plans : plans)?.map((plan, index) => {
                  const key = isEditPlanGroup ? plan.id : `local-${index}`;
                  const isOpen = openPlanEditors.includes(key);
                  return (
                    <div
                      key={String(key)}
                      ref={(el) => {
                        planItemRefs.current.set(key, el);
                      }}
                    >
                    <Accordion 
                      expanded={isOpen}
                      onChange={(_, isExpanded) => {
                        if (isExpanded) {
                          setOpenPlanEditors((prev) => [...prev, key]);
                        } else {
                          setOpenPlanEditors((prev) => prev.filter((k) => k !== key));
                        }
                      }}
                      elevation={isOpen ? 8 : 2}
                      sx={{
                        '& .MuiAccordion-root': {
                          border: '1px solid',
                          borderColor: 'divider',
                        },
                        '&.Mui-expanded': {
                          margin: '16px 0',
                          boxShadow: (theme) => theme.customShadows?.z24 || theme.shadows[8],
                        },
                        '&:before': {
                          display: 'none',
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          bgcolor: isOpen ? 'action.selected' : 'transparent',
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            margin: '16px 0',
                            '&.Mui-expanded': {
                              margin: '16px 0',
                            },
                          },
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Box sx={{ flex: 1, mr: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {plan.meals.map((meal: CommonMealTag) => meal.name_en).join(', ') || 'Untitled Plan'}
                          </Typography>
                          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              {plan.packages.length} Package{plan.packages.length !== 1 ? 's' : ''}: {plan.packages.map((p: { period: number }) => `${p.period}d`).join(', ')}
                            </Typography>
                            {plan.calories && (
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                {plan.calories.calories
                                  ? `${plan.calories.calories} cal`
                                  : `${plan.calories.calories_from}-${plan.calories.calories_to} cal`}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                        
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleDuplicatePlan(plan, index);
                            }}
                            sx={{ 
                              pointerEvents: 'auto',
                              '& .component-iconify': {
                                pointerEvents: 'none'
                              }
                            }}
                          >
                            <Iconify icon="solar:copy-bold" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setPlanToDelete(key);
                              setOpenConfirm(true);
                            }}
                            sx={{ 
                              pointerEvents: 'auto',
                              '& .component-iconify': {
                                pointerEvents: 'none'
                              }
                            }}
                          >
                            <Iconify icon="eva:trash-2-outline" />
                          </IconButton>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails 
                        sx={{ 
                          bgcolor: 'background.neutral',
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          p: 3,
                        }}
                      >
                        <Box sx={{ position: 'relative' }}>
                          <Box 
                            sx={{ 
                              position: 'absolute',
                              left: -16,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              bgcolor: 'divider',
                              borderRadius: 2,
                            }} 
                          />
                          <PlanForm
                            onClose={() => setOpenPlanEditors((prev) => prev.filter((k) => k !== key))}
                            onSave={handlePlanSubmit}
                            initialPlan={plan}
                            onHasChanges={(changed) => markPlanChanged(key, changed)}
                            onDraftChange={(draftKey, draft) => {
                              if (typeof draft === 'undefined') {
                                clearPlanDraft(key);
                                return;
                              }
                              upsertPlanDraft(key, draft);
                            }}
                            draftKey={key}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                    </div>
                  );
                })}

                {isEditPlanGroup && newPlanEditors.map((key) => {
                  const isOpen = openPlanEditors.includes(key);
                  return (
                  <div
                    key={key}
                    ref={(el) => {
                      planItemRefs.current.set(key, el);
                    }}
                  >
                  <Accordion 
                    expanded={isOpen}
                    onChange={(_, isExpanded) => {
                      if (isExpanded) {
                        setOpenPlanEditors((prev) => [...prev, key]);
                      } else {
                        setOpenPlanEditors((prev) => prev.filter((k) => k !== key));
                      }
                    }}
                    elevation={isOpen ? 8 : 2}
                    sx={{
                      '& .MuiAccordion-root': {
                        border: '1px solid',
                        borderColor: 'divider',
                      },
                      '&.Mui-expanded': {
                        margin: '16px 0',
                        boxShadow: (theme) => theme.customShadows?.z24 || theme.shadows[8],
                      },
                      '&:before': {
                        display: 'none',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: isOpen ? 'action.selected' : 'transparent',
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          margin: '16px 0',
                          '&.Mui-expanded': {
                            margin: '16px 0',
                          },
                        },
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          New Plan
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          Configure meals, packages, and calories
                        </Typography>
                      </Box>
                      
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setPlanToDelete(key);
                          setOpenConfirm(true);
                        }}
                        sx={{ 
                          pointerEvents: 'auto',
                          '& .component-iconify': {
                            pointerEvents: 'none'
                          }
                        }}
                      >
                        <Iconify icon="eva:trash-2-outline" />
                      </IconButton>
                    </AccordionSummary>
                    
                    <AccordionDetails 
                      sx={{ 
                        bgcolor: 'background.neutral',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        p: 3,
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            left: -16,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            bgcolor: 'divider',
                            borderRadius: 2,
                          }} 
                        />
                        <PlanForm
                          onClose={() => setOpenPlanEditors((prev) => prev.filter((k) => k !== key))}
                          onSave={handlePlanSubmit}
                          onHasChanges={(changed) => markPlanChanged(key, changed)}
                          onDraftChange={(dk, draft) => {
                            if (typeof draft === 'undefined') {
                              clearPlanDraft(key);
                              return;
                            }
                            upsertPlanDraft(key, draft);
                          }}
                          draftKey={key}
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  </div>
                  );
                })}
              </Stack>
            </Card>
          </Grid>
          
          <Grid xs={12} md={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              {isEditPlanGroup && (
                <>
                  <Button variant="contained" color="error" onClick={confirm.onTrue}>
                    Delete Plan Group
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleDuplicatePlanGroup}
                    startIcon={<Iconify icon="solar:copy-bold" />}
                  >
                    Duplicate
                  </Button>
                </>
              )}

              <LoadingButton 
                type="submit" 
                variant="contained" 
                loading={isSubmitting} 
                form={formId}
                disabled={!hasFormChanges && isEditPlanGroup}
              >
                {isEditPlanGroup ? 'Save Plan Group' : 'Create Plan Group'}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
        </Container>

        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title="Delete"
          content="Are you sure you want to delete this plan group?"
          action={
            <Button variant="contained" color="error" onClick={handleDeletePlanGroup}>
              Delete
            </Button>
          }
        />

        <ConfirmDialog
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          title="Delete Plan"
          content="Are you sure you want to delete this plan?"
          action={
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDeletePlan(planToDelete as any)}
            >
              Delete
            </Button>
          }
        />

      </FormProvider>
    </PermissionBasedGuard>
  );
}